import ArrowDownIcon from '@assets/icons/arrow-down.svg';
import ArrowUpIcon from '@assets/icons/arrow-up.svg';
import i18n from '@/utils/i18n';
import Modal from 'react-native-modal';
import React, { ComponentType, useState } from 'react';
import {
  AllowedShortcutNumbers,
  AllowedShortcuts,
} from '@/platform/shortcuts/operator-shortcuts';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { ScrollView, Text, View } from 'react-native';
import { ShortcutDetailsRow } from './shortcut-details-row';
import { SvgProps } from 'react-native-svg';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';

interface ShortcutItem {
  hotKey: AllowedShortcuts | typeof AllowedShortcutNumbers;
  Icon?: ComponentType<SvgProps>[] | undefined;
}

const distributeIntoColumns = (
  items: ShortcutItem[],
  numColumns: number,
): ShortcutItem[][] => {
  const columns: ShortcutItem[][] = Array.from(
    { length: numColumns },
    () => [],
  );
  items.forEach((item, index) => {
    columns[index % numColumns].push(item);
  });
  return columns;
};

export const KeyboardShortcutsSheet: React.FC = () => {
  const { isSmallTabletLayout, isMediumLayout } = useLayoutContext();

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [styles] = useStyles();

  const onToggleKeyboardShortcutsModal = () => {
    setIsVisible(!isVisible);
  };

  useKeyboardShortcuts(
    AllowedShortcuts.ToggleKeyboardShortcutsModal,
    onToggleKeyboardShortcutsModal,
    [isVisible],
  );

  // Set the number of columns based on the layout. Defaults to 3 columns on desktop
  let numberOfColumns = 3;

  if (isSmallTabletLayout) {
    numberOfColumns = 1;
  } else if (isMediumLayout) {
    numberOfColumns = 2;
  }

  const allShortcuts: AllowedShortcuts[] = Object.values(AllowedShortcuts);

  const shortcutsToDisplay: ShortcutItem[] = allShortcuts
    .filter((value) => value !== AllowedShortcuts.ArrowDown)
    .map((value) => {
      // ArrowDown and ArrowUp are displayed as a single row, so we need to handle them differently
      if (value === AllowedShortcuts.ArrowUp) {
        return {
          hotKey: value,
          Icon: [ArrowUpIcon, ArrowDownIcon],
        };
      } else {
        return { hotKey: value };
      }
    });

  // add AllowedShortcutNumbers back to the shortcutsToDisplay list since it is not included in the AllowedShortcuts enum
  shortcutsToDisplay.push({ hotKey: AllowedShortcutNumbers });

  const columns: ShortcutItem[][] = distributeIntoColumns(
    shortcutsToDisplay,
    numberOfColumns,
  );

  return (
    <Modal
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={isVisible}
      onBackdropPress={() => setIsVisible(false)}
      onModalHide={() => setIsVisible(false)}
      style={styles.modal}
    >
      <ScrollView style={styles.modalContent}>
        <Text style={[FONTS.primaryBold, styles.title]}>
          {i18n.t('keyboard_shortcuts.title')}
        </Text>
        <View
          style={[
            styles.gridContainer,
            isSmallTabletLayout ? styles.gapSmall : styles.gapBig,
          ]}
        >
          {columns.map((column, index) => (
            <View key={index} style={styles.column}>
              {column.map((shortcut: ShortcutItem, idx: number) => (
                <ShortcutDetailsRow
                  hotKey={shortcut.hotKey}
                  Icon={shortcut.Icon}
                  key={idx}
                />
              ))}
            </View>
          ))}
        </View>
      </ScrollView>
    </Modal>
  );
};

const useStyles = createStyles((theme) => ({
  column: {
    flex: 1,
  },
  gapBig: {
    gap: 96,
  },
  gapSmall: {
    gap: 48,
  },
  gridContainer: {
    flexDirection: 'row',
  },
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '30%',
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  title: {
    color: theme.colors.black,
    fontSize: 18,
    marginBottom: 16,
  },
}));
